export const SUPPORTED_LANGUAGES = ["en", "fr", "es", "de", "pt", "zh", "pl", "ja", "el", "id"] as const;

export type SupportedLanguage = (typeof SUPPORTED_LANGUAGES)[number];

export const DEFAULT_LANGUAGE: SupportedLanguage = "en";

/* eslint-disable lingui/no-unlocalized-strings -- each label is intentionally in its own language */
export const LANGUAGE_LABELS: Record<SupportedLanguage, string> = {
  en: "English",
  fr: "Français",
  es: "Español",
  de: "Deutsch",
  pt: "Português (Brasil)",
  zh: "中文（简体）",
  pl: "Polski",
  ja: "日本語",
  el: "Ελληνικά",
  id: "Bahasa Indonesia",
};
/* eslint-enable lingui/no-unlocalized-strings */

export function coerceToSupported(
  value: string | null | undefined,
): SupportedLanguage {
  if (!value) return DEFAULT_LANGUAGE;
  const lower = value.toLowerCase();
  const stripped = lower.split(/[-_]/)[0];
  return (SUPPORTED_LANGUAGES as readonly string[]).includes(stripped)
    ? (stripped as SupportedLanguage)
    : DEFAULT_LANGUAGE;
}

// Parses an RFC 9110 Accept-Language header and returns the highest
// q-weighted supported language. Original order breaks ties. Mirrors the
// server's detectFromAcceptLanguage so the signup default matches the
// language a learner's browser advertises.
export function detectFromAcceptLanguage(
  header: string | null | undefined,
): SupportedLanguage {
  if (!header) return DEFAULT_LANGUAGE;

  type Candidate = { tag: string; q: number; order: number };

  const candidates: Candidate[] = [];
  let order = 0;
  for (const part of header.split(",")) {
    const segments = part
      .trim()
      .split(";")
      .map((s) => s.trim())
      .filter(Boolean);
    if (segments.length === 0) continue;

    const tag = segments[0];
    let q = 1;
    for (let i = 1; i < segments.length; i += 1) {
      const match = /^q\s*=\s*([\d.]+)$/i.exec(segments[i]);
      if (match) {
        const parsed = Number(match[1]);
        if (Number.isFinite(parsed) && parsed >= 0 && parsed <= 1) q = parsed;
      }
    }
    if (q <= 0) continue;

    candidates.push({ tag, q, order: order++ });
  }

  candidates.sort((a, b) => b.q - a.q || a.order - b.order);

  for (const { tag } of candidates) {
    const coerced = coerceToSupported(tag);
    if (
      coerced !== DEFAULT_LANGUAGE ||
      tag.toLowerCase().split(/[-_]/)[0] === DEFAULT_LANGUAGE
    ) {
      return coerced;
    }
  }

  return DEFAULT_LANGUAGE;
}

/* eslint-disable lingui/no-unlocalized-strings -- cookie name + attributes are technical, not UI copy */
export const LOCALE_COOKIE = "ms-locale";

// Reads the persisted locale pick from a request Cookie header. Returns null
// when absent or unsupported so callers can fall back to Accept-Language.
export function parseLocaleCookie(
  cookieHeader: string | null | undefined,
): SupportedLanguage | null {
  if (!cookieHeader) return null;
  for (const part of cookieHeader.split(";")) {
    const eq = part.indexOf("=");
    if (eq === -1) continue;
    const name = part.slice(0, eq).trim();
    if (name !== LOCALE_COOKIE) continue;
    const value = decodeURIComponent(part.slice(eq + 1).trim());
    const stripped = value.toLowerCase().split(/[-_]/)[0];
    return (SUPPORTED_LANGUAGES as readonly string[]).includes(stripped)
      ? (stripped as SupportedLanguage)
      : null;
  }
  return null;
}

// Persists the locale pick client-side so it survives the signup -> onboarding
// redirect (and the OAuth popup round-trip). No-op during SSR.
export function setLocaleCookie(value: string): void {
  if (typeof document === "undefined") return;
  const locale = coerceToSupported(value);
  const maxAge = 60 * 60 * 24 * 365;
  document.cookie = `${LOCALE_COOKIE}=${locale}; path=/; max-age=${maxAge}; samesite=lax`;
}
/* eslint-enable lingui/no-unlocalized-strings */
